import { localCatalog } from '../../data/catalog'
import { openingHours } from '../../data/content'
import { windowFor } from '../../core/rules/schedule'
import { DEFAULT_STAFF_ID } from '../../core/rules/agenda'
import { addDays, iso } from '../../core/format/dates'
import type { AgendaBlock, StaffMember } from '../../core/types/agenda'
import type { AppointmentRow } from '../db'

/**
 * DEMO — een gevulde agenda rond vandaag: afgeronde behandelingen (met producten en notitie),
 * een no-show, een annulering van een klant, een gast en blokkades. Afspraken vallen op echte
 * open dagen, zodat de agenda er op elke dag van de week geloofwaardig uitziet.
 */

export const demoStaff: StaffMember[] = [{ id: DEFAULT_STAFF_ID, name: 'Sarah', initials: 'S' }]

/** De n-de open dag vanaf vandaag (0 = vandaag als het open is, anders de eerstvolgende; negatief = terug). */
function openDay(n: number): string {
  const step = n < 0 ? -1 : 1
  // Vooruit telt vandaag mee als dag 0; terug begint bij gisteren als dag 1.
  let found = n < 0 ? 0 : -1
  for (let i = n < 0 ? -1 : 0; Math.abs(i) <= 60; i += step) {
    const d = addDays(new Date(), i)
    if (!windowFor(openingHours, d.getDay())) continue
    found += 1
    if (found === Math.abs(n)) return iso(d)
  }
  return iso(addDays(new Date(), n))
}

const slug = (wanted: string) => localCatalog.treatments.find((t) => t.slug === wanted)?.slug ?? localCatalog.treatments[0]?.slug ?? wanted
const product = (i: number) => localCatalog.products[i % Math.max(1, localCatalog.products.length)]?.id ?? ''
const stamp = (date: string, time = '09:00') => `${date}T${time}`

export function seedAgendaAppointments(): AppointmentRow[] {
  const rows: AppointmentRow[] = [
    {
      id: 'af-2950',
      customerId: 'klant-1650',
      treatmentSlug: slug('goddess-lift'),
      date: openDay(-1),
      time: '10:00',
      kind: 'salon',
      status: 'bevestigd',
      receivedAt: stamp(openDay(-12)),
      outcome: 'afgerond',
      products: { used: [product(0), product(3)], advice: [product(1), product(4)] },
      treatmentNote: 'Huid reageerde rustig op de lift. Volgende keer iets langer op de kaaklijn.',
    },
    {
      id: 'af-2944',
      customerId: 'klant-2208',
      treatmentSlug: slug('get-the-red-out'),
      date: openDay(-2),
      time: '13:30',
      kind: 'salon',
      status: 'bevestigd',
      receivedAt: stamp(openDay(-15)),
      outcome: 'afgerond',
      products: { used: [product(2)], advice: [product(2), product(5)] },
      treatmentNote: 'Roodheid wangen duidelijk minder dan bij intake.',
    },
    {
      id: 'AF-112',
      guest: { name: 'Marloes de Wit', email: 'marloes@voorbeeld.nl', firstVisit: true, phone: '06 41 22 87 30' },
      treatmentSlug: slug('ibiza-behandeling'),
      date: openDay(-3),
      time: '11:00',
      kind: 'salon',
      status: 'bevestigd',
      receivedAt: stamp(openDay(-9)),
      outcome: 'afgerond',
      products: { used: [product(6)], advice: [] },
    },
    {
      id: 'af-2931',
      customerId: 'klant-1932',
      treatmentSlug: slug('vitamine-c-behandeling'),
      date: openDay(-4),
      time: '15:00',
      kind: 'salon',
      status: 'bevestigd',
      receivedAt: stamp(openDay(-20)),
      outcome: 'no-show',
    },
    {
      id: 'af-3040',
      customerId: 'klant-2317',
      treatmentSlug: slug('persian-delight'),
      date: openDay(1),
      time: '09:30',
      kind: 'salon',
      status: 'bevestigd',
      receivedAt: stamp(openDay(-6)),
      outcome: 'geannuleerd',
      cancelledBy: 'klant',
    },
    {
      id: 'AF-121',
      guest: { name: 'Anouk Jansen', email: 'anouk@voorbeeld.nl', firstVisit: true, phone: '06 18 55 02 44' },
      treatmentSlug: slug('goddess-lift'),
      date: openDay(0),
      time: '13:00',
      kind: 'salon',
      status: 'bevestigd',
      receivedAt: stamp(openDay(-5)),
    },
    {
      id: 'af-3044',
      customerId: 'klant-1932',
      treatmentSlug: slug('vitamine-c-behandeling'),
      date: openDay(0),
      time: '15:30',
      kind: 'salon',
      status: 'bevestigd',
      receivedAt: stamp(openDay(-3)),
    },
    {
      id: 'af-3048',
      customerId: 'klant-2208',
      treatmentSlug: slug('cosmic-botox'),
      date: openDay(2),
      time: '11:00',
      kind: 'salon',
      status: 'bevestigd',
      receivedAt: stamp(openDay(-4)),
    },
    {
      id: 'af-3052',
      customerId: 'klant-2317',
      treatmentSlug: slug('persian-delight'),
      date: openDay(3),
      time: '14:00',
      kind: 'salon',
      status: 'bevestigd',
      receivedAt: stamp(openDay(-1)),
    },
  ]
  return rows.map((r) => ({ staffId: DEFAULT_STAFF_ID, ...r }))
}

export function seedAgendaBlocks(): AgendaBlock[] {
  const pauses = [-3, -2, -1, 0, 1, 2, 3, 4, 5].map<AgendaBlock>((n) => ({
    id: `blok-pauze-${n}`,
    staffId: DEFAULT_STAFF_ID,
    date: openDay(n),
    time: '12:30',
    durationMinutes: 30,
    reason: 'pauze',
  }))
  return [
    ...pauses,
    { id: 'blok-admin', staffId: DEFAULT_STAFF_ID, date: openDay(-1), time: '16:00', durationMinutes: 60, reason: 'administratie', label: 'Bestellingen inpakken' },
    { id: 'blok-tandarts', staffId: DEFAULT_STAFF_ID, date: openDay(4), time: '15:00', durationMinutes: 120, reason: 'privé', label: 'Tandarts' },
  ]
}
